import React, { useState, useEffect } from 'react';
import { AlertTriangle, Eye, EyeOff, StickyNote } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getProjectMeta,
  setProjectUrgent,
  followProject,
  unfollowProject,
} from '@/services/projects.service';
import type { ProjectMeta } from '@/types';
import { Modal } from '@/components/common';
import { ProjectNotesPanel } from './ProjectNotesPanel';

interface ProjectActionsBarProps {
  projectId: number;
  currentUserId?: number;
  // Membru al proiectului sau admin — doar aceștia pot marca „Urgent"
  canSetUrgent?: boolean;
  // Reîmprospătează lista inline din pagină după adăugarea unei note din modal
  onNotesChanged?: () => void;
}

export function ProjectActionsBar({ projectId, currentUserId, canSetUrgent = false, onNotesChanged }: ProjectActionsBarProps) {
  const [meta, setMeta] = useState<ProjectMeta | null>(null);
  const [isUrgentBusy, setIsUrgentBusy] = useState(false);
  const [isFollowBusy, setIsFollowBusy] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  useEffect(() => {
    let active = true;
    getProjectMeta(projectId)
      .then((m) => {
        if (active) setMeta(m);
      })
      .catch((err) => {
        console.error('Failed to load project meta:', err);
      });
    return () => {
      active = false;
    };
  }, [projectId]);

  const isUrgent = meta?.is_urgent ?? false;
  const isFollowing = meta?.is_following ?? false;
  const notesCount = meta?.notes_count ?? 0;

  const handleToggleUrgent = async () => {
    if (!canSetUrgent) {
      toast.error('Doar membrii proiectului pot marca proiectul ca urgent.');
      return;
    }
    const next = !isUrgent;
    setIsUrgentBusy(true);
    try {
      const updated = await setProjectUrgent(projectId, next);
      setMeta((prev) => ({ ...(prev ?? { is_following: false, notes_count: 0 }), ...updated }));
      toast.success(next ? 'Proiect marcat ca URGENT.' : 'Marcajul „Urgent" a fost eliminat.');
    } catch (err) {
      console.error('Failed to toggle urgent:', err);
      toast.error('Nu s-a putut actualiza marcajul „Urgent".');
    } finally {
      setIsUrgentBusy(false);
    }
  };

  const handleToggleFollow = async () => {
    const next = !isFollowing;
    setIsFollowBusy(true);
    try {
      if (next) {
        await followProject(projectId);
      } else {
        await unfollowProject(projectId);
      }
      setMeta((prev) => ({
        ...(prev ?? { is_urgent: false, notes_count: 0 }),
        is_following: next,
      } as ProjectMeta));
      toast.success(next ? 'Urmăriți acest proiect.' : 'Nu mai urmăriți acest proiect.');
    } catch (err) {
      console.error('Failed to toggle follow:', err);
      toast.error('Nu s-a putut actualiza urmărirea.');
    } finally {
      setIsFollowBusy(false);
    }
  };

  const handleNotesCountChange = (count: number) => {
    setMeta((prev) => (prev ? { ...prev, notes_count: count } : prev));
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {/* Urgent — roșu */}
        <button
          type="button"
          onClick={handleToggleUrgent}
          disabled={isUrgentBusy}
          title={
            canSetUrgent
              ? isUrgent
                ? 'Elimină marcajul urgent'
                : 'Marchează proiectul ca urgent'
              : 'Doar membrii proiectului pot marca urgent'
          }
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 ${
            isUrgent
              ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
              : 'bg-white text-red-600 border-red-200 hover:bg-red-50'
          }`}
        >
          <AlertTriangle className="w-4 h-4" />
          {isUrgent ? 'Urgent' : 'Marchează urgent'}
        </button>

        {/* Urmărește — albastru */}
        <button
          type="button"
          onClick={handleToggleFollow}
          disabled={isFollowBusy}
          title={isFollowing ? 'Nu mai urmări proiectul' : 'Urmărește modificările proiectului'}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-50 ${
            isFollowing
              ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
              : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'
          }`}
        >
          {isFollowing ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          {isFollowing ? 'Urmărit' : 'Urmărește'}
        </button>

        {/* Adaugă notă — modal DOAR cu formularul; lista notelor se afișează inline în pagină */}
        <button
          type="button"
          onClick={() => setShowNotes(true)}
          title="Adaugă o notă la proiect"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border bg-white text-amber-700 border-amber-200 hover:bg-amber-50 transition-colors"
        >
          <StickyNote className="w-4 h-4" />
          Adaugă notă
          {notesCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
              {notesCount}
            </span>
          )}
        </button>
      </div>

      <Modal isOpen={showNotes} onClose={() => setShowNotes(false)} title="Adaugă notă" size="lg">
        <ProjectNotesPanel
          projectId={projectId}
          currentUserId={currentUserId}
          mode="add"
          onCountChange={handleNotesCountChange}
          onAdded={() => { setShowNotes(false); onNotesChanged?.(); }}
        />
      </Modal>
    </>
  );
}
